/**
 * Authentication State Caching Utility
 * Helps reduce login screen flash by caching auth state
 */

interface CachedAuthState {
  userId: string
  email: string
  isAuthenticated: boolean
  timestamp: number
  expiresAt: number
}

const AUTH_CACHE_KEY = 'clockit-auth-cache'
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

export class AuthCache {
  static set(userId: string, email: string, isAuthenticated: boolean): void {
    try {
      const cachedState: CachedAuthState = {
        userId,
        email,
        isAuthenticated,
        timestamp: Date.now(),
        expiresAt: Date.now() + CACHE_DURATION
      }
      
      localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(cachedState))
    } catch (error) {
      console.warn('Failed to cache auth state:', error)
    }
  }

  static get(): CachedAuthState | null {
    try {
      const cached = localStorage.getItem(AUTH_CACHE_KEY)
      if (!cached) return null

      const cachedState: CachedAuthState = JSON.parse(cached)
      
      // Check if cache has expired
      if (Date.now() > cachedState.expiresAt) {
        this.clear()
        return null
      }

      return cachedState
    } catch (error) {
      console.warn('Failed to read auth cache:', error)
      this.clear()
      return null
    }
  }

  static clear(): void {
    try {
      localStorage.removeItem(AUTH_CACHE_KEY)
    } catch (error) {
      console.warn('Failed to clear auth cache:', error)
    }
  }

  static isLikelyAuthenticated(): boolean {
    const cached = this.get()
    return cached?.isAuthenticated ?? false
  }
}